/**
 * `/` — Overview.
 *
 * ---------------------------------------------------------------------------
 * THE ONE QUESTION
 * ---------------------------------------------------------------------------
 * Alex opens this on a phone to answer "is anything wrong, and do I need to
 * act". Everything above the fold answers exactly that and nothing else.
 *
 * The hero states the estate verdict on TWO lines, and the second line is the
 * one this codebase exists for:
 *
 *   line 1 — the worst verdict among the sweeps that HAVE been judged
 *   line 2 — how many sweeps have NOT been judged, never folded in
 *
 * "Every judged sweep is green" and "everything is green" are different claims.
 * PLAN §1.2 invokes the agent only on a delta, so a healthy estate legitimately
 * has sweeps that have collected fifty times and been judged once — and a tile
 * that has never been judged is not a healthy tile. A hero that printed a single
 * GREEN over both would be the page-level version of rendering `unproven` as a
 * tick.
 *
 * Line 2 used to be a hatched amber banner carrying a 20-word sentence. It is
 * now a dotted-ring icon, six words, and the sweep names as dotted chips. The
 * CLAIM is identical; it just no longer looks like an incident, because an
 * unjudged sweep is not one.
 *
 * The dead-man outranks both. A stale watcher means every verdict on the page is
 * a claim about the past, so it is stated first and in the hero's own voice.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useApi } from '../lib/api';
import { formatUsd, fullTime, humanizeMs, relativeTime } from '../lib/format';
import { Card, Ident, Metric, Placeholder, Skeleton, StatusPill } from '../components/primitives';
import { Icon } from '../components/icons';
import { SeriesChart } from '../components/SeriesChart';

interface SweepTile {
  kind: string;
  cadence: string;
  usesAgent: boolean;
  lastRun: {
    id: number;
    startedAt: string;
    finishedAt: string | null;
    ok: boolean | null;
    changed: boolean | null;
    agentInvoked: boolean | null;
    error: string | null;
  } | null;
  lastAgentRun: {
    id: string;
    ts: string;
    model: string | null;
    verdict: string | null;
    summary: string | null;
    usd: number | null;
    error: string | null;
  } | null;
  verdictAbsentReason: string | null;
  next: { dueNow: boolean; reason: string; inMs: number | null; at: string | null };
}

export interface Overview {
  generatedAt: string;
  ledgerEmpty: boolean;
  sweeps: SweepTile[];
  findings: { open: number; byStatus: Record<string, number>; bySeverity: Record<string, number> };
  approvals: { pending: number; expired: number };
  deadman: {
    state: string;
    wouldSend: boolean;
    lastSuccessfulTriageAt: string | null;
    staleMs: number | null;
    thresholdMs: number;
  };
  spend: { last24hUsd: number; runs24h: number };
}

/**
 * Why a sweep has no verdict.
 *
 * These are TOOLTIPS now, not body copy. The row itself says "no verdict" or
 * "none by design" in two words; the sentence is one hover (or one long-press)
 * away, and the general rule lives in the reading key. A dashboard that
 * explains itself under every row has no room left to show anything.
 */
const ABSENT_REASON: Record<string, string> = {
  never_run: 'this sweep has never run',
  no_agent_run: 'the sweep has run but no agent has judged it yet',
  agent_errored: 'the last agent run recorded no verdict',
  // Not a defect: PLAN §3 gives `self-check` the model "none".
  deterministic: 'deterministic by design — this sweep never invokes an agent',
};

const VERDICT_RANK: Record<string, number> = { RED: 3, AMBER: 2, GREEN: 1 };

interface EstateState {
  /** The status string the hero renders through the normal vocabulary. */
  status: string;
  headline: string;
  /** The sweep that produced the worst verdict, for the "why". */
  worst: SweepTile | null;
  judged: number;
  /** Sweeps that COULD have a verdict and do not. `deterministic` excluded. */
  unjudged: SweepTile[];
}

function estateStateOf(data: Overview): EstateState {
  const judged = data.sweeps.filter((s) => s.lastAgentRun?.verdict != null);
  const unjudged = data.sweeps.filter(
    (s) => s.lastAgentRun?.verdict == null && s.verdictAbsentReason !== 'deterministic',
  );
  const worst = judged.reduce<SweepTile | null>((acc, sweep) => {
    const rank = VERDICT_RANK[sweep.lastAgentRun?.verdict ?? ''] ?? 0;
    const accRank = VERDICT_RANK[acc?.lastAgentRun?.verdict ?? ''] ?? 0;
    return rank > accRank ? sweep : acc;
  }, null);

  // The dead-man outranks every verdict: if the watcher is stale, the freshest
  // GREEN on the page is a statement about a cluster that stopped being watched.
  if (data.deadman.wouldSend) {
    return { status: 'error', headline: 'NOT WATCHING', worst, judged: judged.length, unjudged };
  }

  if (judged.length === 0) {
    return { status: 'unproven', headline: 'NO VERDICT', worst: null, judged: 0, unjudged };
  }

  return {
    status: worst?.lastAgentRun?.verdict ?? 'unproven',
    headline: worst?.lastAgentRun?.verdict ?? 'NO VERDICT',
    worst,
    judged: judged.length,
    unjudged,
  };
}

/**
 * The hero's hue.
 *
 * GREEN is the ONE place in the application where the good colour is spent, and
 * it is spent here because this single word is the answer to the only question
 * the page exists to answer. Everywhere else an `ok` is drawn in ink.
 */
function headlineClass(status: string): string {
  switch (status) {
    case 'GREEN':
      return 'text-good';
    case 'AMBER':
      return 'text-warn';
    case 'RED':
    case 'error':
      return 'text-bad';
    default:
      return 'text-ink-2';
  }
}

export function OverviewPage({
  data,
  navigate,
}: {
  data: Overview | null;
  navigate: (to: string) => void;
}): ReactNode {
  if (data === null) return <LoadingSkeleton />;

  if (data.ledgerEmpty) {
    return (
      <div className="space-y-5">
        <Placeholder status="empty" title="the ledger is empty">
          No sweep has run yet, so there is nothing to judge. This is what first boot looks like —
          the scheduler runs <code>self-check</code> and <code>db-ha</code> within five minutes.
        </Placeholder>
        <DeadmanCard data={data} />
      </div>
    );
  }

  const estate = estateStateOf(data);
  const critical = data.findings.bySeverity['critical'] ?? 0;

  return (
    <div className="space-y-5 md:space-y-6">
      <EstateHero data={data} estate={estate} navigate={navigate} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric
          label="open findings"
          value={data.findings.open}
          icon="findings"
          sub={data.findings.open === 0 ? 'nothing in the ledger' : 'in the ledger'}
          onClick={() => {
            navigate('/findings');
          }}
        />
        <Metric
          label="critical"
          value={critical}
          icon="errors"
          tone={critical === 0 ? 'neutral' : 'bad'}
          sub={critical === 0 ? 'none at critical' : 'need a decision'}
          onClick={() => {
            navigate('/findings?severity=critical');
          }}
        />
        <Metric
          label="approvals"
          value={data.approvals.pending}
          icon="checks"
          sub={
            data.approvals.expired > 0
              ? `${String(data.approvals.expired)} expired unanswered`
              : /*
                 * `Approve / Reject` is one indivisible phrase — it names the two
                 * buttons in the Telegram message. Left to wrap freely at 390 px
                 * it broke as `awaiting Approve /` + `Reject`, which reads as a
                 * dangling operator rather than a pair of choices.
                 */
                <>
                  awaiting <span className="whitespace-nowrap">Approve / Reject</span>
                </>
          }
        />
        <Metric
          label="spend 24 h"
          value={formatUsd(data.spend.last24hUsd)}
          icon="cost"
          sub={`${String(data.spend.runs24h)} agent ${data.spend.runs24h === 1 ? 'run' : 'runs'}`}
        />
      </div>

      <SweepLedger data={data} navigate={navigate} />

      <DeadmanCard data={data} />

      <div className="grid gap-5 xl:grid-cols-2">
        <SeriesChart name="scrape_targets_up" title="Scrape targets up" defaultRange="24h" />
        <SeriesChart name="alerts_active" title="Active alert series" defaultRange="24h" maxSeries={6} />
      </div>

      <p className="pt-1 text-center text-2xs text-ink-3">
        generated {fullTime(data.generatedAt)} · all times Europe/Bucharest
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The hero
 * ------------------------------------------------------------------ */

function EstateHero({
  data,
  estate,
  navigate,
}: {
  data: Overview;
  estate: EstateState;
  navigate: (to: string) => void;
}): ReactNode {
  const summary = estate.worst?.lastAgentRun?.summary ?? null;
  const runId = estate.worst?.lastAgentRun?.id ?? null;

  return (
    <section
      aria-label="estate verdict"
      className="overflow-hidden rounded-card border border-line bg-surface"
    >
      <div className="p-5 sm:p-7">
        <p className="text-2xs font-medium uppercase tracking-[0.14em] text-ink-3">
          state of the estate
        </p>

        <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h2 className={`text-2xl font-semibold tracking-tight ${headlineClass(estate.status)}`}>
            {estate.headline}
          </h2>
          <p className="text-xs text-ink-3">
            worst of <span className="num font-medium text-ink-2">{estate.judged}</span> judged{' '}
            {estate.judged === 1 ? 'sweep' : 'sweeps'}
          </p>
        </div>

        {/*
          LINE 2 — the claim the hero is NOT making. One line, dotted chips,
          drawn in ink: an unjudged sweep is the normal operation of a
          delta-triggered agent, not an incident, and it must not compete with
          the verdict above it. It is still never merged into that verdict.
        */}
        {estate.unjudged.length > 0 ? (
          <div className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-2">
            <span className="flex items-center gap-1.5 text-xs text-ink-3">
              <Icon name="unproven" size={14} className="text-ink-4" />
              {estate.unjudged.length} of {data.sweeps.length} sweeps carry no verdict
            </span>
            <ul className="flex flex-wrap gap-1.5">
              {estate.unjudged.map((sweep) => (
                <li key={sweep.kind}>
                  <span
                    className="epi-unmeasured rounded-md border px-2 py-0.5 text-2xs text-ink-3"
                    title={ABSENT_REASON[sweep.verdictAbsentReason ?? ''] ?? undefined}
                  >
                    {sweep.kind}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="mt-4 text-xs text-ink-3">Every sweep that can be judged has been judged.</p>
        )}

        {data.deadman.wouldSend && (
          <p className="mt-5 flex items-start gap-2 rounded-lg border border-bad/40 px-3 py-2.5 text-xs leading-relaxed text-bad">
            <Icon name="error" size={15} className="mt-px" />
            <span>
              No triage sweep has succeeded for{' '}
              {data.deadman.staleMs === null
                ? 'longer than the threshold'
                : humanizeMs(data.deadman.staleMs)}{' '}
              (threshold {humanizeMs(data.deadman.thresholdMs)}). Every verdict on this page is
              older than that.
            </span>
          </p>
        )}

        {summary !== null && (
          <p className="mt-5 max-w-prose border-t border-line pt-4 text-base leading-relaxed text-ink-2">
            {summary}
          </p>
        )}
      </div>

      {runId !== null && (
        <button
          type="button"
          onClick={() => {
            navigate(`/runs/${runId}`);
          }}
          className="flex w-full items-center justify-between gap-2 border-t border-line px-5 py-3.5 text-left text-xs text-ink-3 transition-colors hover:bg-surface-2 hover:text-ink sm:px-7"
        >
          <span>open the {estate.worst?.kind ?? ''} run behind this verdict</span>
          <Icon name="chevron" size={15} />
        </button>
      )}
    </section>
  );
}

/**
 * A sweep's agent summary, in the ledger row.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A COMPONENT AND NOT A `line-clamp-2`
 * ---------------------------------------------------------------------------
 * It used to be one, and on a 390 px phone it produced `…astro-ai has returned
 * no…` — a sentence guillotined mid-clause. An ellipsis in the middle of a
 * clause does not read as "there is more"; it reads as a rendering fault, and an
 * operator who has learned to distrust the rendering will distrust the numbers
 * next to it.
 *
 * The truncation is therefore made DELIBERATE and REVERSIBLE: when the text is
 * actually cut, a control appears that opens it in place, so the ellipsis is
 * something the reader can undo rather than damage they discovered.
 *
 * ---------------------------------------------------------------------------
 * WHY IT MEASURES INSTEAD OF COUNTING CHARACTERS
 * ---------------------------------------------------------------------------
 * The first version of this offered the control whenever the summary passed 150
 * characters. That is viewport-blind, and the desktop screenshot showed exactly
 * what viewport-blind costs: a summary sitting complete on one line, with
 * `show the whole summary` underneath it. The control was lying — the whole
 * summary was already there — and a control that lies about the state of the
 * thing it controls is worse than the truncation it was added to fix.
 *
 * So the clamp is measured, on the real element, at the real width: the control
 * exists only when `scrollHeight` actually exceeds `clientHeight`. It appears on
 * the phone, where the text is cut, and never on the desktop, where it is not.
 */
function SweepSummary({ text }: { text: string }): ReactNode {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const [open, setOpen] = useState(false);
  const [clipped, setClipped] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const measure = (): void => {
      // Only meaningful while collapsed: with the clamp off the two heights are
      // equal by definition, and measuring then would retract the control.
      if (!open) setClipped(element.scrollHeight > element.clientHeight + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [open, text]);

  return (
    <div className="mt-2">
      <p
        ref={ref}
        className={`text-xs leading-relaxed text-ink-3 ${open ? '' : 'line-clamp-2'}`}
      >
        {text}
      </p>
      {clipped && (
        <button
          type="button"
          onClick={() => {
            setOpen((v) => !v);
          }}
          aria-expanded={open}
          className="text-xs font-medium text-ink-2 underline decoration-line-strong underline-offset-2 transition-colors hover:text-ink"
        >
          {open ? 'show less' : 'show the whole summary'}
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The sweep ledger
 * ------------------------------------------------------------------ */

/**
 * One row per sweep, not one card per sweep.
 *
 * Six cards of identical weight is six things competing for the eye, which is
 * exactly the composition that hides the one that matters. A row list gives the
 * verdict column a single vertical scan line: the AMBER is found without
 * reading — and it is now the only coloured chip in that column, because a
 * GREEN verdict and an unjudged sweep are both drawn in ink.
 */
function SweepLedger({
  data,
  navigate,
}: {
  data: Overview;
  navigate: (to: string) => void;
}): ReactNode {
  return (
    <Card title="Sweeps" icon="snapshot" padding="flush">
      <ul className="divide-y divide-line border-t border-line">
        {data.sweeps.map((sweep) => {
          const verdict = sweep.lastAgentRun?.verdict ?? null;
          const absent = sweep.verdictAbsentReason ?? '';
          const clickable = sweep.lastAgentRun !== null;
          const facts: ReactNode[] = [
            <span key="cadence">{sweep.cadence}</span>,
            sweep.lastRun === null ? (
              <span key="last" className="text-ink-3">
                never run
              </span>
            ) : (
              <span key="last" className="num" title={fullTime(sweep.lastRun.startedAt)}>
                {relativeTime(sweep.lastRun.startedAt)}
                {sweep.lastRun.ok === false && (
                  <span className="ml-1.5 font-medium text-bad">failed</span>
                )}
              </span>
            ),
            <span key="next" className="num">
              {sweep.next.dueNow
                ? 'due now'
                : sweep.next.inMs === null
                  ? sweep.next.reason
                  : `next in ${humanizeMs(sweep.next.inMs)}`}
            </span>,
            sweep.lastAgentRun?.usd == null ? null : (
              <span key="cost" className="num">
                {formatUsd(sweep.lastAgentRun.usd)}
              </span>
            ),
            sweep.lastAgentRun?.model == null ? null : (
              <span key="model" className="mono text-[11px]">
                {sweep.lastAgentRun.model}
              </span>
            ),
          ].filter((node) => node !== null);

          /*
           * The NAVIGATING part of the row only. The summary and the error line
           * sit outside it, because the summary is itself a disclosure control
           * and a `<button>` inside a `<button>` is invalid HTML that browsers
           * resolve by dropping one of them.
           */
          const head = (
            <>
              <div className="flex items-start justify-between gap-3">
                <p className="truncate text-base font-medium text-ink">{sweep.kind}</p>
                <span className="shrink-0">
                  {verdict !== null ? (
                    <StatusPill status={verdict} />
                  ) : (
                    <StatusPill
                      status={absent === 'deterministic' ? 'empty' : 'unproven'}
                      title={ABSENT_REASON[absent] ?? undefined}
                    >
                      {absent === 'deterministic' ? 'none by design' : 'no verdict'}
                    </StatusPill>
                  )}
                </span>
              </div>

              {/* One line of metadata, dot-separated. Four labelled columns of
                  micro-type said the same thing in four times the space. */}
              <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-ink-3">
                {facts.map((fact, index) => (
                  <span key={index} className="flex items-center gap-1.5">
                    {index > 0 && <span className="text-ink-4">·</span>}
                    {fact}
                  </span>
                ))}
              </p>
            </>
          );

          return (
            <li key={sweep.kind} className="px-5 py-4">
              {clickable ? (
                <button
                  type="button"
                  onClick={() => {
                    navigate(`/runs/${sweep.lastAgentRun?.id ?? ''}`);
                  }}
                  className="-mx-2 block w-full rounded-lg px-2 text-left transition-colors hover:bg-surface-2"
                >
                  {head}
                </button>
              ) : (
                head
              )}

              {sweep.lastAgentRun?.summary != null && (
                <SweepSummary text={sweep.lastAgentRun.summary} />
              )}
              {sweep.lastRun?.error != null && (
                <p className="mt-2 flex items-start gap-1.5 text-2xs leading-relaxed text-bad">
                  <Icon name="error" size={13} className="mt-px" />
                  {sweep.lastRun.error}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Dead-man
 * ------------------------------------------------------------------ */

function DeadmanCard({ data }: { data: Overview }): ReactNode {
  const dm = data.deadman;
  const fraction =
    dm.staleMs === null || dm.thresholdMs <= 0 ? null : Math.min(1, dm.staleMs / dm.thresholdMs);
  return (
    <Card
      title="Dead-man"
      icon="deadman"
      right={<StatusPill status={dm.wouldSend ? 'error' : 'ok'}>{dm.state}</StatusPill>}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 text-xs">
        <p className="text-ink-3">
          last successful triage{' '}
          {dm.lastSuccessfulTriageAt === null ? (
            <span className="font-medium text-ink-2">never</span>
          ) : (
            <span className="num font-medium text-ink" title={fullTime(dm.lastSuccessfulTriageAt)}>
              {relativeTime(dm.lastSuccessfulTriageAt)}
            </span>
          )}
        </p>
        <p className="num text-ink-3">
          {dm.staleMs === null ? 'staleness not measurable' : humanizeMs(dm.staleMs)} of{' '}
          {humanizeMs(dm.thresholdMs)}
        </p>
      </div>
      {fraction !== null && (
        <div
          className="mt-3 h-1 overflow-hidden rounded-full bg-white/7"
          role="img"
          aria-label={`staleness ${humanizeMs(dm.staleMs ?? 0)} of a ${humanizeMs(dm.thresholdMs)} threshold`}
        >
          <div
            className={`h-full rounded-full ${
              fraction >= 1 ? 'bg-bad' : fraction >= 0.66 ? 'bg-warn' : 'bg-ink-2'
            }`}
            style={{ width: `${String(fraction * 100)}%` }}
          />
        </div>
      )}
      {dm.staleMs === null && (
        <p className="mt-2 text-2xs text-ink-3">
          Staleness is not measurable because no triage sweep has ever succeeded — this is not a
          staleness of zero.
        </p>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Loading
 * ------------------------------------------------------------------ */

/**
 * A skeleton, not the word "loading…".
 *
 * The shapes match what is coming, so the layout does not jump — and a skeleton
 * that never resolves looks unmistakably different from an empty ledger, which
 * is a distinction this dashboard has to make everywhere.
 */
function LoadingSkeleton(): ReactNode {
  return (
    <div className="space-y-5">
      <Skeleton label="the overview" shapes={['h-48']} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-card border border-line bg-surface" />
        ))}
      </div>
      <div
        aria-hidden="true"
        className="h-64 animate-pulse rounded-card border border-line bg-surface"
      />
    </div>
  );
}

export function useOverview(): ReturnType<typeof useApi<Overview>> {
  return useApi<Overview>('/api/overview', 30_000);
}

/** Re-exported so `App.tsx` can render the case-file style ident consistently. */
export { Ident };
